
let _token = null;
let _user = null;
let _orgRole = null;
let _refreshTimer = null;
const _listeners = {};

function _emit(event, detail) {
	(_listeners[event] || []).forEach(fn => fn(detail));
}

function _parseJwt(token) {
	try {
		const payload = token.split('.')[1];
		return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
	} catch { return null; }
}

function _scheduleRefresh() {
	if (_refreshTimer) clearTimeout(_refreshTimer);
	_refreshTimer = null;
	if (!_token) return;
	const payload = _parseJwt(_token);
	if (!payload || !payload.exp) return;
	const ms = (payload.exp - Math.floor(Date.now() / 1000) - 60) * 1000;
	if (ms > 0) {
		_refreshTimer = setTimeout(() => refreshToken(), ms);
	}
}

export function getToken() { return _token; }

export function setToken(token) {
	_token = token;
	if (token) {
		const payload = _parseJwt(token);
		if (payload) _user = { id: payload.id || payload.sub, username: payload.username, email: payload.email, systemRole: payload.systemRole || 'user' };
	}
	updateXHRFactoryHeaders();
	_scheduleRefresh();
}

export function clearToken() {
	_token = null;
	_user = null;
	_orgRole = null;
	if (_refreshTimer) { clearTimeout(_refreshTimer); _refreshTimer = null; }
	updateXHRFactoryHeaders();
}

export function getUser() { return _user; }
export function isAuthenticated() { return !!_token; }
export function isAdmin() { return _user?.systemRole === 'admin'; }
export function getOrgRole() { return _orgRole; }
export function setOrgRole(role) { _orgRole = role; }

export function addEventListener(event, fn) {
	if (!_listeners[event]) _listeners[event] = [];
	_listeners[event].push(fn);
}

export function removeEventListener(event, fn) {
	if (!_listeners[event]) return;
	_listeners[event] = _listeners[event].filter(f => f !== fn);
}

export async function login(username, password) {
	const res = await fetch('/api/auth/login', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		credentials: 'include',
		body: JSON.stringify({ username, password }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({ error: 'Login failed' }));
		throw new Error(err.error || 'Login failed');
	}
	const data = await res.json();
	setToken(data.accessToken || data.access_token);
	_emit('auth:login', { user: _user });
	return data;
}

export async function logout() {
	try {
		await fetch('/api/auth/logout', {
			method: 'POST',
			credentials: 'include',
			headers: _token ? { 'Authorization': `Bearer ${_token}` } : {},
		});
	} catch { /* ignore */ }
	clearToken();
	_emit('auth:logout', {});
}

export async function refreshToken() {
	const res = await fetch('/api/auth/refresh', {
		method: 'POST',
		credentials: 'include',
	});
	if (!res.ok) {
		clearToken();
		_emit('auth:logout', { reason: 'refresh_failed' });
		throw new Error('Token refresh failed');
	}
	const data = await res.json();
	setToken(data.accessToken || data.access_token);
	return data;
}

export async function apiFetch(url, options = {}) {
	const headers = { ...(options.headers || {}) };
	if (_token) headers['Authorization'] = `Bearer ${_token}`;
	let res = await fetch(url, { ...options, headers, credentials: 'include' });
	if (res.status === 401) {
		try {
			await refreshToken();
			headers['Authorization'] = `Bearer ${_token}`;
			res = await fetch(url, { ...options, headers, credentials: 'include' });
		} catch {
			throw new Error('Authentication required');
		}
	}
	return res;
}

export async function authFetch(url, options = {}) {
	const headers = { ...(options.headers || {}) };
	if (_token) headers['Authorization'] = `Bearer ${_token}`;
	return fetch(url, { ...options, headers, credentials: 'include' });
}

export function createAuthGetter(url) {
	return async (begin, end) => {
		const headers = { 'Range': `bytes=${begin}-${end - 1}` };
		if (_token) headers['Authorization'] = `Bearer ${_token}`;
		const res = await fetch(url, { headers, credentials: 'include' });
		// The Copc reader's Binary.toDataView assumes the getter returns a
		// typed array view (reads .buffer/.byteOffset/.length). Returning a
		// raw ArrayBuffer breaks Header.parse with a DataView constructor
		// error, so wrap it in a Uint8Array view.
		return new Uint8Array(await res.arrayBuffer());
	};
}

export function updateXHRFactoryHeaders() {
	if (typeof Potree === 'undefined' || !Potree.XHRFactory) return;
	const config = Potree.XHRFactory.config;
	if (_token) {
		const existing = config.customHeaders.find(h => h.header === 'Authorization');
		if (existing) {
			existing.value = `Bearer ${_token}`;
		} else {
			config.customHeaders.push({ header: 'Authorization', value: `Bearer ${_token}` });
		}
	} else {
		config.customHeaders = config.customHeaders.filter(h => h.header !== 'Authorization');
	}
}

export const AuthManager = {
	getToken, setToken, clearToken,
	getUser, isAuthenticated, isAdmin, getOrgRole, setOrgRole,
	login, logout, refreshToken,
	apiFetch, authFetch, createAuthGetter,
	updateXHRFactoryHeaders,
	addEventListener, removeEventListener,
};
